import React from 'react';
import { useState } from 'react';
import {
  Dialog,
  Disclosure,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  ShoppingCartIcon,
  CreditCardIcon,
  ChatBubbleBottomCenterTextIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';
import LogoLang from './LogoLang';
import { Helmet } from 'react-helmet';

// Produkte-Subnavigation
const products = [
  {
    name: 'Alle Produkte',
    description: 'Entdecke unsere digitalen Visitenkarten',
    href: '/produkte',
    icon: InformationCircleIcon,
  },
  {
    name: 'Basic-Karte',
    description: 'Hochwertige digitale Visitenkarte',
    href: '/produkte/65db4f2519422cfaf902c453',
    icon: CreditCardIcon,
  },
  {
    name: 'Custom-Karte',
    description: 'Individuelle, beidseitig bedruckte NFC-Visitenkarte',
    href: '/produkte/65db4f9319422cfaf902c455',
    icon: CreditCardIcon,
  },
];

// Über uns-Subnavigation
const aboutUs = [
  {
    name: 'Unternehmen',
    description: 'Unsere Geschichte und Vision',
    href: 'https://www.svisit.ch/ueber-uns',
    icon: BuildingOfficeIcon,
  },
  {
    name: 'Blog',
    description: 'Aktuelle Beiträge und Anleitungen zu digitalen Visitenkarten',
    href: 'https://www.svisit.ch/blog',
    icon: ChatBubbleBottomCenterTextIcon,
  },
  {
    name: 'Kontakt',
    description: 'Schicke uns eine Nachricht',
    href: 'https://www.svisit.ch/kontakt',
    icon: PhoneIcon,
  },
];

const navigation = [
  { name: 'Features', href: 'https://www.svisit.ch/#features' },
  { name: 'Preise', href: 'https://www.svisit.ch/#preise' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // User from local storage
  const userObject = localStorage.getItem('user');
  let user;
  if (userObject) {
    user = JSON.parse(userObject);
    if (user.timestamp) {
      const storedTimestamp = new Date(user.timestamp);
      const currentTime = new Date();

      const timeDifference = currentTime - storedTimestamp;

      const oneDayInMs = 24 * 60 * 60 * 1000;

      if (timeDifference < oneDayInMs) {
        user.ok = true;
      } else {
        user.ok = false;
      }
    } else {
      console.log('Kein Timestamp gefunden.');
    }
  }

  return (
    <header className="absolute inset-x-0 top-0 z-50">
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
        aria-label="Global"
      >
        <div className="flex lg:flex-1">
          <Link to="/" className="-m-1.5 p-1.5">
            <span className="sr-only">SVisit</span>
            <LogoLang />
          </Link>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <PopoverGroup className="hidden lg:flex lg:gap-x-12">
          {/* Normale Navigationslinks */}
          {navigation.map((item) => (
            <a
              key={item.name}
              href={item.href}
              className="text-sm font-semibold leading-6 text-gray-900"
            >
              {item.name}
            </a>
          ))}

          {/* Produkte Dropdown */}
          <Popover className="relative">
            <PopoverButton className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900">
              Produkte
              <ChevronDownIcon
                className="h-5 w-5 flex-none text-gray-400"
                aria-hidden="true"
              />
            </PopoverButton>

            <PopoverPanel className="absolute top-full -left-8 z-10 mt-3 w-screen max-w-md overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5">
              <div className="p-4">
                {products.map((item) => (
                  <div
                    key={item.name}
                    className="group relative flex items-center gap-x-6 rounded-lg p-4 text-sm hover:bg-gray-50"
                  >
                    <div className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                      <item.icon
                        className="h-6 w-6 text-gray-600 group-hover:text-svisit-primary-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="flex-auto">
                      <a
                        href={item.href}
                        className="block font-semibold text-gray-900"
                      >
                        {item.name}
                        <span className="absolute inset-0" />
                      </a>
                      <p className="mt-1 text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </PopoverPanel>
          </Popover>

          {/* Über uns Dropdown */}
          <Popover className="relative">
            <PopoverButton className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900">
              Über uns
              <ChevronDownIcon
                className="h-5 w-5 flex-none text-gray-400"
                aria-hidden="true"
              />
            </PopoverButton>

            <PopoverPanel className="absolute top-full -left-8 z-10 mt-3 w-screen max-w-md overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5">
              <div className="p-4">
                {aboutUs.map((item) => (
                  <div
                    key={item.name}
                    className="group relative flex items-center gap-x-6 rounded-lg p-4 text-sm hover:bg-gray-50"
                  >
                    <div className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                      <item.icon
                        className="h-6 w-6 text-gray-600 group-hover:text-svisit-primary-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="flex-auto">
                      <a
                        href={item.href}
                        className="block font-semibold text-gray-900"
                      >
                        {item.name}
                        <span className="absolute inset-0" />
                      </a>
                      <p className="mt-1 text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </PopoverPanel>
          </Popover>
        </PopoverGroup>

        <div className="hidden lg:flex lg:flex-1 lg:justify-end gap-x-6 items-center">
          {/* Warenkorb */}
          <Link to="/warenkorb">
            <button
              type="button"
              className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Warenkorb ansehen</span>
              <ShoppingCartIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </Link>

          {/* Separator */}
          <div
            className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200"
            aria-hidden="true"
          />
          {/* Wenn eingeloggt, nur Dashboard anzeigen */}
          {user?.ok === true ? (
            <Link
              to="/dashboard"
              className="rounded-sm bg-svisit-primary-600 px-2 py-1 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
            >
              Zu deinem Account <span aria-hidden="true">&rarr;</span>
            </Link>
          ) : (
            <>
              <Link
                to="/register"
                className="rounded-sm bg-svisit-primary-600 px-2 py-1 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
              >
                Registrieren
              </Link>
              <Link
                to="/login"
                className="text-sm font-semibold leading-6 text-gray-900"
              >
                Einloggen <span aria-hidden="true">&rarr;</span>
              </Link>
            </>
          )}
        </div>
      </nav>
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-50" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <a href="#" className="-m-1.5 p-1.5">
              <span className="sr-only">sVisit</span>
              <LogoLang />
            </a>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {/* Normale Navigationslinks im Mobile-Menü */}
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                  >
                    {item.name}
                  </Link>
                ))}

                {/* Produkte Dropdown im Mobile-Menü */}
                <Disclosure as="div" className="-mx-3">
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
                        Produkte
                        <ChevronDownIcon
                          className={`h-5 w-5 flex-none ${
                            open ? 'rotate-180' : ''
                          }`}
                          aria-hidden="true"
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="mt-2 space-y-2">
                        {products.map((item) => (
                          <Disclosure.Button
                            key={item.name}
                            as="a"
                            href={item.href}
                            className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                          >
                            {item.name}
                          </Disclosure.Button>
                        ))}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>

                {/* Über uns Dropdown im Mobile-Menü */}
                <Disclosure as="div" className="-mx-3">
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
                        Über uns
                        <ChevronDownIcon
                          className={`h-5 w-5 flex-none ${
                            open ? 'rotate-180' : ''
                          }`}
                          aria-hidden="true"
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="mt-2 space-y-2">
                        {aboutUs.map((item) => (
                          <Disclosure.Button
                            key={item.name}
                            as="a"
                            href={item.href}
                            className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                          >
                            {item.name}
                          </Disclosure.Button>
                        ))}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              </div>

              <div className="py-6 space-y-2 ">
                <Link
                  to="/warenkorb"
                  className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                >
                  Warenkorb
                </Link>
                {/* Wenn eingeloggt, nur Dashboard anzeigen */}
                {user?.ok === true ? (
                  <Link
                    to="/dashboard"
                    className="bg-svisit-primary-600 -mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
                  >
                    Zu deinem Account
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                    >
                      Einloggen
                    </Link>
                    <Link
                      to="/register"
                      className="bg-svisit-primary-600 -mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
                    >
                      Registrieren
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
      {/* Helmet Head Tags */}
      <Helmet>
        <script
          id="Cookiebot"
          src="https://consent.cookiebot.com/uc.js"
          data-cbid="3f0dcc82-68c8-451e-9b44-239b1faa38e8"
          type="text/javascript"
        ></script>
      </Helmet>
    </header>
  );
};

export default Header;
